import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isNull, isUndefined } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryModel, InventoryPaths } from 'client/data/models/inventory';
import { CARMAX } from 'site-modules/shared/constants/providers-360';

const FALLBACK_TRACKING_CREATIVE_ID = 'edm-inventory-hero-image';

function With360PhotoUI({ photo360 = null, carmax360 = undefined, photoUrls = [], children }) {
  const [isFallback, setIsFallback] = useState(false);
  const [hasExteriorImages, setHasExteriorImages] = useState(false);
  const [interiorImages, setInteriorImages] = useState(null);

  const hasExterior = get(photo360, 'hasExterior');
  const hasInterior = get(photo360, 'hasInterior');
  const provider360 = get(photo360, 'provider');
  const isProviderCarmax360 = provider360 === CARMAX;

  const fallback = useCallback(() => {
    if (isEmpty(interiorImages)) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: TrackingConstant.FALLBACK_NO_360,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          creative_id: FALLBACK_TRACKING_CREATIVE_ID,
        },
      });

      setIsFallback(true);
    }
    setHasExteriorImages(false);
  }, [interiorImages]);

  useEffect(() => {
    if (!isProviderCarmax360 || isUndefined(carmax360)) return;
    if (isNull(carmax360)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- state update driven by async model data, not cascading render
      fallback();
      return;
    }
    const interiorThreeSixtyImages =
      hasInterior && carmax360 ? carmax360.filter(item => item.type === '360-images') : null;

    if (!hasExterior && isEmpty(interiorThreeSixtyImages)) {
      fallback();
    } else {
      setHasExteriorImages(hasExterior);
      setInteriorImages(interiorThreeSixtyImages);
    }
    // eslint-disable-next-line
  }, [carmax360]);

  const hasPhoto360 = (isProviderCarmax360 && !isFallback) || (!!photo360 && !isProviderCarmax360);
  const carmaxFallback = isProviderCarmax360 && !isFallback ? fallback : null;
  const photoUrlsFull = hasPhoto360 ? [photoUrls[0], ...photoUrls] : photoUrls;
  let photoInfo = null;
  if (hasPhoto360) {
    photoInfo = isProviderCarmax360 ? { ...photo360, hasExteriorImages, interiorImages } : photo360;
  }

  return children(hasPhoto360, photoInfo, photoUrlsFull, carmaxFallback);
}

export const stateToPropsConfig = {
  carmax360: bindToPath(
    ({ stockNum, photo360 }) => (get(photo360, 'provider') === CARMAX ? InventoryPaths.getCarmax360(stockNum) : null),
    InventoryModel,
    null,
    false
  ),
};

With360PhotoUI.propTypes = {
  photo360: PropTypes.shape({ provider: PropTypes.string, hasExterior: PropTypes.bool, hasInterior: PropTypes.bool }),
  carmax360: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string })),
  photoUrls: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string, photoType: PropTypes.string })),
  children: PropTypes.func.isRequired,
};

export const With360Photo = connectToModel(With360PhotoUI, stateToPropsConfig);
